package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** A push selects a subset of an explicit approval, never mutable current renders. */
@Serializable
data class ShotPushRequest(val approvalId: String, val locales: List<String>, val devices: List<DeviceClass>)

/** Durable states distinguish waiting for an editable store version from terminal failures. */
@Serializable
enum class ShotPushState { QUEUED, RUNNING, WAITING_FOR_VERSION, SUCCEEDED, PARTIAL, FAILED, CANCELLED }

/** Per-set progress distinguishes unchanged images and recovered failures. */
@Serializable
enum class ShotPushItemState { PENDING, UPLOADING, SENT, SAME, FAILED, RESTORED, SKIPPED }

/** A successful write is independent of whether a subsequent store read confirms it. */
@Serializable
enum class ShotPushVerification { NOT_CHECKED, VERIFIED, MISMATCH }

/** Store locale mapping and image counts remain visible for each device set. */
@Serializable
data class ShotPushItemDto(
    val locale: String, val storeLocale: String, val deviceClass: DeviceClass, val state: ShotPushItemState,
    val sent: Int = 0, val same: Int = 0, val removed: Int = 0, val message: String? = null,
    val verification: ShotPushVerification = ShotPushVerification.NOT_CHECKED,
    val verificationReason: String? = null,
)

/** Aggregates preserve the difference between failed and restored sets. */
@Serializable
data class ShotPushTotalsDto(val sets: Int, val sent: Int, val same: Int, val failed: Int, val restored: Int)

/** Polling a durable job does not depend on the original request remaining connected. */
@Serializable
data class ShotPushJobDto(
    val id: String, val platform: StorePlatform, val state: ShotPushState, val approvalId: String,
    val createdBy: String, val createdAt: String, val startedAt: String? = null, val finishedAt: String? = null,
    val queuedReason: String? = null, val retryAt: String? = null, val committedNote: String? = null,
    val errorCode: String? = null, val items: List<ShotPushItemDto>, val totals: ShotPushTotalsDto,
)

/** Preflight checks share stable names across server, review and push screens. */
@Serializable
enum class ShotPushCheckCode {
    STORE_CONNECTED, APP_BOUND, VAULT_CONFIGURED, ROLE, APPROVAL_VALID, APPROVAL_COVERS,
    CAPTION_APPROVED, CAPTION_UNCHANGED, FIT, CAPTURE_CURRENT, RENDER_CURRENT, SIZE, ALPHA,
    COUNT_PER_SET, MIN_PER_PLATFORM, VERSION_EDITABLE,
    CAPTION_CONTRAST, THUMBNAIL_LEGIBILITY, CAPTION_SAFE_AREA, READABILITY_UNCHECKED,
    DESIGN_VERSION_UNAVAILABLE,
}

/** Severity is explicit so colour is never the sole carrier of status. */
@Serializable
enum class ShotCheckSeverity { GREEN, AMBER, RED }

/** Optional evidence preserves decoding of older preflight responses. Heights are estimates. */
@Serializable
data class ShotReadabilityDto(
    val layerId: String? = null, val contrastRatio: Double? = null, val requiredContrast: Double? = null,
    val thumbnailWidth: Int? = null, val estimatedInkHeightPx: Double? = null,
)

/** Repair paths keep a blocked push connected to the responsible editor cell. */
@Serializable
data class ShotPushCheckDto(
    val code: ShotPushCheckCode, val severity: ShotCheckSeverity, val message: String,
    val cell: RenderCellRef? = null, val fixPath: String? = null,
    val readability: ShotReadabilityDto? = null,
)

/** Store editability is distinct from whether the selected renders satisfy preflight. */
@Serializable
data class ShotPushPreflightDto(
    val platform: StorePlatform, val pushable: Boolean, val editable: Boolean,
    val stateNote: String? = null, val checks: List<ShotPushCheckDto>,
)
